import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { Spinner } from './Spinner';

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  icon?: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  width?: number;
}

/** Design confirmation dialog: 20px radius, icon on top, text-button cancel, filled (or error) confirm. */
export function ConfirmDialog({
  open,
  title,
  children,
  icon,
  confirmLabel,
  cancelLabel,
  destructive,
  loading,
  onConfirm,
  onCancel,
  width = 440,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      role="alertdialog"
      slotProps={{ paper: { sx: { width, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      {icon && (
        <Box sx={{ px: 3, pt: 3, color: destructive ? 'error.main' : 'primary.main' }}>
          <Icon name={icon} size={24} />
        </Box>
      )}
      <DialogTitle sx={icon ? { pt: 2 } : undefined}>{title}</DialogTitle>
      {children && (
        <DialogContent sx={{ color: 'text.secondary', textWrap: 'pretty' }}>{children}</DialogContent>
      )}
      <DialogActions>
        <Button variant="text" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <Spinner /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
